import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMailStore } from '@/stores/mail';
import { useAuthStore } from '@/stores/auth';
import { useOnlineStatus } from '@/hooks/use-online';
import { sendMessage, replyMessage, forwardMessage, getMessage, getThread } from '@/api/messages';
import { replyRecipients } from '@/lib/reply-recipients';
import { replyQuote, forwardQuote } from '@/lib/quote';
import { getDrafts, saveDraft, deleteDraft } from '@/api/inbox';
import { ComposeEditor, type Attachment } from '@/components/compose-editor';
import { ForwardAttachmentChoice } from '@/components/forward-attachment-choice';
import { Check, Send, X, ExternalLink, Minus } from 'lucide-react';

// Snapshot of everything the composer needs to move between windows.
export interface PopoutHandoff {
  nonce: string;
  mailboxId: string;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  attachments: Attachment[];
  draftId: string | null;
  reply: string | null;
  forward: string | null;
  /** The forward carries the whole conversation (contact-link forward picker). */
  forwardThread?: boolean;
  includeOriginalAttachments?: boolean | null;
}

// Soft two-note "sent" chime via Web Audio (no asset needed). One shared context,
// reused — browsers (esp. iOS) suspend a context created outside a user gesture, so
// unlockAudio() must be called from the click handler; the chime then plays from the
// async success callback.
let _audioCtx: AudioContext | null = null;
function getAudioCtx(): AudioContext | null {
  try {
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext);
    if (!Ctx) return null;
    if (!_audioCtx) _audioCtx = new Ctx();
    return _audioCtx;
  } catch { return null; }
}
function unlockAudio() {
  const ctx = getAudioCtx();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
}
function playSentSound() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const now = ctx.currentTime;
  ([[587.33, 0], [880, 0.09]] as [number, number][]).forEach(([freq, t]) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.value = freq;
    o.connect(g); g.connect(ctx.destination);
    g.gain.setValueAtTime(0.0001, now + t);
    g.gain.exponentialRampToValueAtTime(0.18, now + t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.28);
    o.start(now + t); o.stop(now + t + 0.3);
  });
}

function SendingOverlay() {
  // After a few seconds silence starts to feel like a hang, so the animation
  // gains words. Not a fake step list — just an honest note that it's slow.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-bmail-navy/85 backdrop-blur-sm"
         role="status" aria-live="polite">
      <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-bmail-goldfill/15 text-bmail-gold">
        <span className="bmail-trail absolute right-1/2 h-[2px] rounded bg-bmail-gold/60" />
        <Send size={38} className="bmail-fly" />
      </div>
      <div className="text-lg font-semibold text-bmail-text">Sending</div>
      {slow && (
        <div className="max-w-xs text-center text-sm text-bmail-text-secondary">
          Still going — the mail server is taking its time. Nothing is lost; this window stays until it's done.
        </div>
      )}
    </div>
  );
}

export function ComposeRoute({ popout = false, handoff }: { popout?: boolean; handoff?: PopoutHandoff } = {}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { mailboxes, selectedMailbox } = useMailStore();
  const [mailboxId, setMailboxId] = useState(handoff?.mailboxId || selectedMailbox?.id || mailboxes[0]?.id || '');
  const [to, setTo] = useState(handoff?.to ?? '');
  const [cc, setCc] = useState(handoff?.cc ?? '');
  const [bcc, setBcc] = useState(handoff?.bcc ?? '');
  const [subject, setSubject] = useState(handoff?.subject ?? '');
  const [body, setBody] = useState(handoff?.body ?? '');
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>(handoff?.attachments ?? []);
  const [draftId, setDraftId] = useState<string | null>(handoff?.draftId ?? null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [includeOriginalAttachments, setIncludeOriginalAttachments] = useState<boolean | null>(handoff?.includeOriginalAttachments ?? null);

  const reply = handoff ? handoff.reply : params.get('reply');
  // 🚩 Reply all rides on the EXISTING `reply` ref with a flag beside it, rather than a second
  // `replyall` param — `parseRef` and the message query stay untouched, and there is only ever one
  // code path loading the original.
  const replyAll = handoff ? false : params.get('all') === '1';
  const forward = handoff ? handoff.forward : params.get('forward');
  const from = params.get('from');
  const draft = params.get('draft');
  // From a tapped address in an email body (contact-action-popover): who it goes to, and for a
  // forward picked there, whether the whole conversation goes along.
  const toParam = handoff ? null : params.get('to');
  const forwardThread = handoff ? !!handoff.forwardThread : params.get('thread') === '1';

  // Clear attachments when navigating to a new compose context (not when the
  // window just received a handoff — those attachments are the point of it).
  useEffect(() => {
    if (handoff) return;
    setAttachments([]);
    setIncludeOriginalAttachments(null);
  }, [reply, forward, forwardThread, from, draft, handoff]);

  // "New email from this mailbox" (sidebar mail icon) preselects the sender.
  useEffect(() => {
    if (from) setMailboxId(from);
  }, [from]);

  // "New message to this address": fill To once. A reply, forward or draft fills it itself.
  useEffect(() => {
    if (handoff || !toParam || reply || forward || draft) return;
    setTo(toParam);
  }, [handoff, toParam, reply, forward, draft]);

  // Load a saved draft into the editor (?draft=<id> from the Drafts folder)
  useEffect(() => {
    if (handoff) return; // pop-out windows arrive pre-loaded
    if (!draft) { setDraftId(null); return; }
    getDrafts().then(({ drafts }) => {
      const d = drafts.find((x) => x.id === draft);
      if (!d) return;
      setDraftId(d.id);
      setTo(d.to.join(', '));
      setCc((d.cc || []).join(', '));
      setBcc((d.bcc || []).join(', '));
      setSubject(d.subject);
      setBody(d.bodyHtml);
      if (d.mailboxId) setMailboxId(d.mailboxId);
      // [023] Restore attachments saved with the draft (base64 round-trips as-is).
      if (d.attachments?.length) setAttachments(d.attachments.map(a => ({
        filename: a.filename,
        contentType: a.contentType || 'application/octet-stream',
        content: a.content,
        size: a.size
      })));
    }).catch(() => {});
  }, [draft]);

  // Load original message for reply/forward to pre-fill fields
  // "mbid/uid[/folder]" — the folder (e.g. [Gmail]/Spam) may itself contain '/',
  // so everything after the second slash is the folder. It disambiguates a Junk
  // UID that collides with an INBOX UID.
  const parseRef = (ref: string | null) => {
    if (!ref) return { mbid: null, uid: null, folder: null };
    const [mbid, uidStr, ...rest] = ref.split('/');
    return { mbid, uid: parseInt(uidStr, 10), folder: rest.join('/') || null };
  };
  const { mbid: replyMbid, uid: replyUid, folder: replyFolder } = parseRef(reply);
  const { mbid: forwardMbid, uid: forwardUid, folder: forwardFolder } = parseRef(forward);

  const { data: replyData } = useQuery({
    queryKey: ['message', replyMbid, replyUid, replyFolder],
    queryFn: () => replyMbid && replyUid ? getMessage(replyMbid, replyUid, replyFolder) : Promise.resolve(null),
    enabled: !!reply
  });

  const { data: forwardThreadData, isError: forwardThreadFailed, refetch: reloadForwardThread } = useQuery({
    queryKey: ['thread', forwardMbid, forwardUid, forwardFolder],
    queryFn: () => getThread(forwardMbid!, forwardUid!, forwardFolder),
    enabled: !!forward && forwardThread && !!forwardMbid && !!forwardUid
  });

  const { data: forwardData, isError: forwardMessageFailed, refetch: reloadForwardMessage } = useQuery({
    queryKey: ['message', forwardMbid, forwardUid, forwardFolder],
    queryFn: () => forwardMbid && forwardUid ? getMessage(forwardMbid, forwardUid, forwardFolder) : Promise.resolve(null),
    enabled: !!forward
  });

  // [Brian 2026-10-27] Ask only for forwards whose original message(s) have files.
  // Wait for source metadata before sending; a loading response must not mean "no files".
  const forwardLoadFailed = forwardMessageFailed || (forwardThread && forwardThreadFailed);
  const forwardSourceReady = !!forwardData?.message && (!forwardThread || !!forwardThreadData);
  const forwardHasAttachments = forwardThread
    ? !!forwardThreadData?.members.some(message => message.hasAttachments)
    : !!(forwardData?.message?.attachments?.length || forwardData?.message?.hasAttachments);

  // Pre-fill fields when original message loads — once per reply/forward. 🔴 [B-08, 2026-10-15] This
  // re-ran on every From change (and mailbox-list refresh), snapping From back to the original's
  // mailbox and overwriting what had been typed. Quotes are escaped text now (B-21, lib/quote.ts).
  const prefilled = useRef<string | null>(null);
  // Leaving a reply/forward for plain compose and coming back (Back button) pre-fills again.
  useEffect(() => { if (!reply && !forward) prefilled.current = null; }, [reply, forward]);
  useEffect(() => {
    if (handoff) return;
    if (!(reply && replyData?.message && mailboxes.length)) return;
    const key = `reply:${reply}:${replyAll ? 'all' : 'one'}`;
    if (prefilled.current === key) return;
    prefilled.current = key;
    const msg = replyData.message;
    // 🔴 [2026-10-01] This used to be `setTo(from)` plus the original Cc copied straight into Cc —
    // a HALF reply-all: everyone on Cc kept the answer while everyone on To was silently dropped.
    // Both buttons go through one rule now, which also drops my own address and never repeats a
    // recipient. See lib/reply-recipients.ts; the shapes it has to parse are measured there.
    const picked = replyRecipients(msg, mailboxes.map((m) => m.email), replyAll);
    setTo(picked.to.join(', '));
    setCc(picked.cc.join(', '));
    setSubject(msg.subject ? (msg.subject.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`) : 'Re:');
    setBody(replyQuote(msg));
    if (replyMbid) setMailboxId(replyMbid);
  }, [handoff, reply, replyAll, replyData, mailboxes, replyMbid]);

  useEffect(() => {
    if (handoff) return;
    if (!(forward && forwardData?.message)) return;
    if (forwardThread && !forwardThreadData) {
      // Nothing is filled rather than a single-message quote over a send that would attach the
      // whole conversation; say why instead of showing a blank "Forward conversation".
      if (forwardThreadFailed) setSendError('Couldn’t load this conversation. Go back and pick it again, or forward a single message.');
      return;
    }
    const key = `forward:${forward}:${forwardThread ? 'thread' : 'one'}:${toParam || ''}`;
    if (prefilled.current === key) return;
    prefilled.current = key;
    const msg = forwardData.message;
    setTo(toParam || '');
    setSubject(msg.subject ? (/^fwd:/i.test(msg.subject) ? msg.subject : `Fwd: ${msg.subject}`) : 'Fwd:');
    // A conversation quotes each message, oldest first; original files are optional attachments.
    setBody(forwardThread && forwardThreadData ? forwardThreadData.members.map(forwardQuote).join('') : forwardQuote(msg));
    if (forwardMbid) setMailboxId(forwardMbid);
  }, [handoff, forward, forwardThread, forwardThreadData, forwardThreadFailed, toParam, forwardData, forwardMbid]);

  const saveDraftMutation = useMutation({
    mutationFn: async () => {
      const res = await saveDraft({
        id: draftId || undefined,
        mailboxId: mailboxId || null,
        to: to.split(',').map((s) => s.trim()).filter(Boolean),
        cc: cc.split(',').map((s) => s.trim()).filter(Boolean),
        bcc: bcc.split(',').map((s) => s.trim()).filter(Boolean),
        subject,
        bodyHtml: body,
        // [023] Attachments ride along so saved drafts round-trip fully.
        attachments: attachments.map(a => ({ filename: a.filename, contentType: a.contentType, content: a.content }))
      });
      setDraftId(res.id);
      return res;
    },
    onSuccess: () => {
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 2000);
    }
  });

  const sendMutation = useMutation({
    mutationFn: () => {
      if (!navigator.onLine) {
        return Promise.reject(new Error('You’re offline — reconnect to send.'));
      }
      // OAuth tier guard (2026-08-24): read-only mailboxes can't send.
      const chosen = mailboxes.find((m) => m.id === mailboxId);
      if (chosen?.accessTier === 'readonly') {
        return Promise.reject(new Error('This mailbox is connected as read-only. Reconnect it with Read + Send or Full access to send mail.'));
      }
      // Attach the sending mailbox's footer/signature (Settings → Footers).
      const sig = mailboxes.find((m) => m.id === mailboxId)?.signature;
      const escSig = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
      let html = sig && sig.trim() ? `${body}<br><br>-- <br>${escSig(sig)}` : body;
      // Default text (Settings → Default text): wrap the whole body so every send
      // carries the user's chosen font. Client-safe: inline style on a plain div.
      const { user } = useAuthStore.getState();
      // Client-side allowlist/clamp mirrors the backend: never trust persisted values.
      const SAFE_FONTS = ['Helvetica','Arial','Verdana','Tahoma','Trebuchet MS','Times New Roman','Georgia','Courier New'];
      const font = user?.defaultFont && SAFE_FONTS.includes(user.defaultFont) ? user.defaultFont : null;
      const sizeNum = Number(user?.defaultFontSize);
      const size = Number.isInteger(sizeNum) && sizeNum >= 8 && sizeNum <= 28 ? sizeNum : null;
      if (font || size) {
        const fam = font ? `font-family:'${font}',Arial,sans-serif;` : '';
        const sz = size ? `font-size:${size}px;` : '';
        html = `<div style="${fam}${sz}">${html}</div>`;
      }
      const attPayload = attachments.length > 0 ? { attachments: attachments.map(a => ({ filename: a.filename, contentType: a.contentType, content: a.content })) } : {};
      const payload = { to: to.split(',').map((s) => s.trim()).filter(Boolean), cc: cc.split(',').map((s) => s.trim()).filter(Boolean), bcc: bcc.split(',').map((s) => s.trim()).filter(Boolean), subject, html, ...attPayload };
      // B-08: the original stays where it is; the mail goes out as the mailbox picked in From.
      if (reply && replyMbid && replyUid) {
        return replyMessage(replyMbid, replyUid, { ...payload, fromMailboxId: mailboxId }, replyFolder);
      }
      if (forward && forwardMbid && forwardUid) {
        if (!forwardSourceReady) return Promise.reject(new Error('The original email has not loaded yet.'));
        if (forwardHasAttachments && includeOriginalAttachments === null) return Promise.reject(new Error('Choose whether to include attachments from the original email.'));
        return forwardMessage(forwardMbid, forwardUid, { ...payload, includeOriginalAttachments: forwardHasAttachments && includeOriginalAttachments === true, fromMailboxId: mailboxId, ...(forwardThread ? { thread: true } : {}) }, forwardFolder);
      }
      return sendMessage(mailboxId, payload);
    },
    onSuccess: () => {
      playSentSound();
      setSent(true);
      // The draft graduated — remove it from the Drafts folder.
      if (draftId) deleteDraft(draftId).catch(() => {});
      // Pop-out window: close itself after the chime; main window: leave compose.
      setTimeout(() => { popout ? window.close() : navigate('/inbox'); }, 1100);
    },
    onError: (e: any) => {
      const d = e?.data?.error;
      setSendError(typeof d === 'string' ? d : 'Couldn’t send — check the recipient email address and try again.');
    }
  });

  // Cancel: discard the email in progress. If anything's been written (or a
  // saved draft exists), confirm first so a stray tap can't eat work. Deleting
  // the saved draft keeps the Drafts folder free of abandoned ghosts.
  const handleCancel = () => {
    const hasContent = !!(to.trim() || cc.trim() || bcc.trim() || subject.trim() || body.trim() || attachments.length > 0 || draftId);
    const discard = () => {
      if (draftId) deleteDraft(draftId).catch(() => {});
      popout ? window.close() : navigate('/inbox');
    };
    if (!hasContent || window.confirm('Discard this email?')) discard();
  };

  // ---- Pop-out / dock handoff between windows ----
  const buildPayload = useCallback((nonce: string | null): PopoutHandoff => ({
    nonce: nonce || crypto.randomUUID(),
    mailboxId, to, cc, bcc, subject, body, attachments, draftId, reply, forward, forwardThread, includeOriginalAttachments
  }), [mailboxId, to, cc, bcc, subject, body, attachments, draftId, reply, forward, forwardThread, includeOriginalAttachments]);

  const saveDraftNow = useCallback(async () => {
    const res = await saveDraft({
      id: draftId || undefined,
      mailboxId: mailboxId || null,
      to: to.split(',').map((s) => s.trim()).filter(Boolean),
      cc: cc.split(',').map((s) => s.trim()).filter(Boolean),
      bcc: bcc.split(',').map((s) => s.trim()).filter(Boolean),
      subject,
      bodyHtml: body,
      attachments: attachments.map((a) => ({ filename: a.filename, contentType: a.contentType, content: a.content }))
    });
    setDraftId(res.id);
    return res.id as string;
  }, [draftId, mailboxId, to, cc, bcc, subject, body, attachments]);

  // Pop out: hand the live editor state to a real browser window. We save the
  // draft first — the server is the source of truth if anything crashes — then
  // hand state over by nonce so the new window opens with the same content.
  const [popping, setPopping] = useState(false);
  const popOut = async () => {
    if (popping || sendMutation.isPending) return;
    setPopping(true);
    try {
      if (draftId) await saveDraftNow(); // only when a draft already exists; brand-new unsent mail isn't a draft yet
      const payload = buildPayload(null);
      try { localStorage.setItem(`bmail-popout-handoff:${payload.nonce}`, JSON.stringify(payload)); } catch {}
      const url = `/compose-popout?h=${payload.nonce}`;
      const w = window.open(url, `bmail-compose-${payload.nonce}`, 'width=720,height=860,left=120,top=80');
      if (!w) { window.alert('Pop-up blocked — allow pop-ups for this site to float the editor.'); return; }
      // Hand the live tab back to the inbox; the pop-out window owns the draft now.
      navigate('/inbox');

      // Re-share the payload if the pop-out window races past the first write
      // (e.g. booted before the storage write flushed). Also tell the pop-out
      // when the main window closes so it doesn't try a silent handback.
      if ('BroadcastChannel' in window) {
        const bc = new BroadcastChannel('bmail-compose');
        const freshPayload = buildPayload(payload.nonce);
        bc.postMessage({ type: 'handoff', nonce: payload.nonce, payload: freshPayload });
        const onMsg = (ev: MessageEvent) => {
          if (ev.data?.type === 'handoff-request' && ev.data.nonce === payload.nonce) {
            bc.postMessage({ type: 'handoff', nonce: payload.nonce, payload: buildPayload(payload.nonce) });
          }
        };
        bc.addEventListener('message', onMsg);
        window.addEventListener('beforeunload', () => {
          bc.postMessage({ type: 'main-window-closing', nonce: payload.nonce });
          bc.close();
        }, { once: true });
      }
    } finally {
      setPopping(false);
    }
  };

  // Dock: hand the composer back to the main window. We save first so the
  // main window / Drafts folder already has the latest content, then the main
  // window's dock listener (App.tsx) navigates to /compose?draft=<id> and
  // focuses itself. If the main window is gone, open a fresh tab — the draft
  // is saved either way, so nothing is ever lost.
  const [docking, setDocking] = useState(false);
  const dockBack = async () => {
    if (docking || sendMutation.isPending || saveDraftMutation.isPending) return;
    setDocking(true);
    try {
      const id = await saveDraftNow();
      const hasOpener = !!(window.opener && !window.opener.closed);
      if (hasOpener) {
        if ('BroadcastChannel' in window) {
          const bc = new BroadcastChannel('bmail-compose');
          bc.postMessage({ type: 'dock-request', draftId: id });
          setTimeout(() => bc.close(), 500);
        }
        try { window.opener.focus(); } catch {}
      } else {
        window.open(`/compose?draft=${id}`, '_blank');
      }
      window.close();
      // If the browser refuses window.close() (some restrict it to script-opened
      // windows), the user just closes this tab — draft is saved, so it's safe.
    } finally {
      setDocking(false);
    }
  };

  // In the pop-out window, listen for the main window going away so we don't
  // attempt a silent handback into a dead tab on future docks.
  const dockBcRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (!popout || !('BroadcastChannel' in window)) return;
    const bc = new BroadcastChannel('bmail-compose');
    dockBcRef.current = bc;
    const onMsg = (ev: MessageEvent) => {
      if (ev.data?.type === 'main-window-closing') {
        try { bc.close(); } catch {}
        dockBcRef.current = null;
      }
    };
    bc.addEventListener('message', onMsg);
    return () => { bc.removeEventListener('message', onMsg); try { bc.close(); } catch {} };
  }, [popout]);

  // PWA Phase 2: don't attempt sends while offline — fail fast with a clear
  // message instead of a network-error soup. (Drafts are server-side too, so
  // saving drafts offline also won't work; the send guard covers the main path.)
  const online = useOnlineStatus();

  return (
    <div className="relative flex h-full flex-col">
      {sendMutation.isPending && !sent && <SendingOverlay />}
      {sent && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-bmail-navy/85 backdrop-blur-sm">
          <div style={{ animation: 'bmail-pop .3s ease-out' }} className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500/20 text-bmail-success">
            <Check size={44} />
          </div>
          <div className="text-lg font-semibold text-bmail-text">Message sent</div>
        </div>
      )}
      <div className="border-b border-bmail-border px-4 py-3 md:px-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-bmail-text">{reply ? 'Reply' : forward ? (forwardThread ? 'Forward conversation' : 'Forward') : 'New message'}</h2>
          </div>
          <div className="flex items-center gap-2">
            <select
              aria-label="From"
              value={mailboxId}
              onChange={(e) => setMailboxId(e.target.value)}
              className="rounded-lg border border-bmail-border bg-bmail-surface px-3 py-1.5 text-sm text-bmail-text w-full sm:w-auto"
            >
              {mailboxes.map((m) => (
                <option key={m.id} value={m.id}>{m.displayName || m.email}</option>
              ))}
            </select>
            {!popout ? (
              <>
                <button
                  onClick={popOut}
                  disabled={popping || sendMutation.isPending}
                  className="flex shrink-0 items-center gap-1.5 rounded-lg border border-bmail-border px-3 py-1.5 text-sm font-medium text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-text disabled:opacity-50 transition-colors"
                  title="Open this email in its own window so you can keep editing while doing other things"
                >
                  <ExternalLink size={16} />
                  {popping ? 'Opening…' : 'Pop out'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={sendMutation.isPending}
                  className="flex shrink-0 items-center gap-1.5 rounded-lg border border-bmail-border px-3 py-1.5 text-sm font-medium text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-text disabled:opacity-50 transition-colors"
                  title="Close and discard this email"
                >
                  <X size={16} />
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={dockBack}
                  disabled={docking || sendMutation.isPending}
                  className="flex shrink-0 items-center gap-1.5 rounded-lg border border-bmail-border px-3 py-1.5 text-sm font-medium text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-text disabled:opacity-50 transition-colors"
                  title="Save and return this email to the main b-mail window"
                >
                  <Minus size={16} />
                  {docking ? 'Docking…' : 'Dock'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={sendMutation.isPending}
                  className="flex shrink-0 items-center gap-1.5 rounded-lg border border-bmail-border px-3 py-1.5 text-sm font-medium text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-text disabled:opacity-50 transition-colors"
                  title="Close and discard this email"
                >
                  <X size={16} />
                  Discard
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {forward && forwardLoadFailed && (
        <div role="alert" className="mx-4 mt-2 rounded-lg bg-bmail-error/10 px-4 py-2 text-sm text-bmail-error md:mx-6">
          Couldn’t load the original email.
          <button className="ml-2 underline" onClick={() => {
            setSendError(null);
            void reloadForwardMessage();
            if (forwardThread) void reloadForwardThread();
          }}>Retry loading original</button>
        </div>
      )}

      {sendError && (
        <div className="mx-4 mt-2 rounded-lg bg-bmail-error/10 px-4 py-2 text-sm text-bmail-error md:mx-6">{sendError}</div>
      )}

      {forward && forwardHasAttachments && <ForwardAttachmentChoice value={includeOriginalAttachments} onChange={setIncludeOriginalAttachments} />}

      <div className="flex-1 overflow-hidden">
        <ComposeEditor
          to={to}
          setTo={setTo}
          cc={cc}
          setCc={setCc}
          bcc={bcc}
          setBcc={setBcc}
          subject={subject}
          setSubject={setSubject}
          body={body}
          onBodyChange={setBody}
          onSend={() => {
            if (forward && !forwardSourceReady) {
              if (!forwardLoadFailed) setSendError('The original email has not loaded yet. Please wait and try again.');
              return;
            }
            if (forward && forwardHasAttachments && includeOriginalAttachments === null) {
              setSendError('Choose whether to include attachments from the original email.');
              document.getElementById('forward-attachments-yes')?.focus();
              return;
            }
            unlockAudio(); setSendError(null); sendMutation.mutate();
          }}
          onSaveDraft={() => saveDraftMutation.mutate()}
          draftSaving={saveDraftMutation.isPending}
          draftSaved={draftSaved}
          isSending={sendMutation.isPending}
          attachments={attachments}
          setAttachments={setAttachments}
        />
      </div>
    </div>
  );
}